import { useState, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { recipes, type RecipeData } from '@/data/mockData';
import { useRecipesData } from '@/hooks/useRecipesData';

import TodayRecommendationCard from '@/components/recipes/TodayRecommendationCard';
import RecipeSearchBar from '@/components/recipes/RecipeSearchBar';
import RecipeCategoryFilters from '@/components/recipes/RecipeCategoryFilters';
import RecipeBenefitFilters from '@/components/recipes/RecipeBenefitFilters';
import RecipeCollections from '@/components/recipes/RecipeCollections';
import RecipeCard from '@/components/recipes/RecipeCard';
import RecipeDetailView from '@/components/recipes/RecipeDetailView';
import GuideSectionCard from '@/components/recipes/GuideSectionCard';
import FavoritesList from '@/components/recipes/FavoritesList';

type TabKey = 'recetas' | 'guia' | 'favoritos';

const Recipes = () => {
  const { loading, favorites, toggleFavorite, getRecommendedRecipes, getRecommendationTitle, getRecommendationSubtitle } = useRecipesData();

  const [activeTab, setActiveTab] = useState<TabKey>('recetas');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('todas');
  const [benefitFilters, setBenefitFilters] = useState<string[]>([]);
  const [selectedRecipe, setSelectedRecipe] = useState<string | null>(null);
  const [collectionFilter, setCollectionFilter] = useState<string[] | null>(null);

  const toggleBenefit = (key: string) => {
    setBenefitFilters(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const filtered = useMemo(() => {
    let result = recipes;

    // Collection filter takes priority
    if (collectionFilter) {
      result = result.filter(r => collectionFilter.includes(r.id));
    } else {
      // Search — match title, description, ingredients, benefitTag
      if (search) {
        const q = search.toLowerCase();
        result = result.filter(r =>
          r.title.toLowerCase().includes(q) ||
          r.description.toLowerCase().includes(q) ||
          r.benefitTag.toLowerCase().includes(q) ||
          r.ingredients.some(ing => ing.toLowerCase().includes(q))
        );
      }
      // Category
      if (category !== 'todas') {
        result = result.filter(r => r.category === category);
      }
      // Benefit tags
      if (benefitFilters.length > 0) {
        result = result.filter(r => benefitFilters.some(bf => r.benefitTags.includes(bf)));
      }
    }
    return result;
  }, [search, category, benefitFilters, collectionFilter]);

  // Detail view
  const recipe = selectedRecipe ? recipes.find(r => r.id === selectedRecipe) : null;
  if (recipe) {
    return (
      <RecipeDetailView
        recipe={recipe}
        isFavorite={favorites.includes(recipe.id)}
        onToggleFavorite={() => toggleFavorite(recipe.id)}
        onBack={() => setSelectedRecipe(null)}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="w-full px-3 pt-3 pb-24 max-w-2xl mx-auto space-y-2 animate-fade-in">
      {/* Header — compact, avoids FloatingProfile */}
      <div className="pr-14">
        <h1 className="text-lg font-heading font-bold leading-tight">Recetas y Guía</h1>
        <p className="text-[11px] text-muted-foreground">Apoyo simple para tu rutina diaria</p>
      </div>

      {/* Tabs — flexible wrapping for narrow screens */}
      <div className="flex flex-wrap gap-1 bg-muted rounded-lg p-1">
        <button
          onClick={() => { setActiveTab('recetas'); setCollectionFilter(null); }}
          className={cn(
            "flex-1 min-w-[90px] py-2 text-[12px] font-medium rounded-md transition-all text-center",
            activeTab === 'recetas' ? "bg-card shadow-card text-foreground" : "text-muted-foreground hover:text-foreground"
          )}
        >
          Recetas
        </button>
        <button
          onClick={() => { setActiveTab('guia'); setCollectionFilter(null); }}
          className={cn(
            "flex-1 min-w-[90px] py-2 text-[12px] font-medium rounded-md transition-all text-center",
            activeTab === 'guia' ? "bg-card shadow-card text-foreground" : "text-muted-foreground hover:text-foreground"
          )}
        >
          Guía
        </button>
        <button
          onClick={() => { setActiveTab('favoritos'); setCollectionFilter(null); }}
          className={cn(
            "flex-1 min-w-[100px] py-2 text-[12px] font-medium rounded-md transition-all text-center flex items-center justify-center gap-1",
            activeTab === 'favoritos' ? "bg-card shadow-card text-foreground" : "text-muted-foreground hover:text-foreground"
          )}
        >
          <span>Favoritos</span>
          {favorites.length > 0 && (
            <span className="text-[10px] bg-primary/10 text-primary px-1.5 py-px rounded-full font-bold">
              {favorites.length}
            </span>
          )}
        </button>
      </div>

      {/* ========== TAB 1: Recetas ========== */}
      {activeTab === 'recetas' && (
        <div className="space-y-2">
          {/* Recommendation card */}
          <TodayRecommendationCard
            title={getRecommendationTitle()}
            subtitle={getRecommendationSubtitle()}
            recipes={getRecommendedRecipes()}
            onSelect={setSelectedRecipe}
          />

          {/* Search */}
          <RecipeSearchBar value={search} onChange={(v) => { setSearch(v); setCollectionFilter(null); }} />

          {/* Category + Benefit filters stacked tight */}
          <RecipeCategoryFilters selected={category} onSelect={(c) => { setCategory(c); setCollectionFilter(null); }} />
          <RecipeBenefitFilters selected={benefitFilters} onToggle={(k) => { toggleBenefit(k); setCollectionFilter(null); }} />

          {/* Collections */}
          {!collectionFilter && !search && (
            <RecipeCollections onSelectCollection={(ids) => { setCollectionFilter(ids); setCategory('todas'); setBenefitFilters([]); setSearch(''); }} />
          )}

          {/* Collection active indicator */}
          {collectionFilter && (
            <div className="flex items-center justify-between bg-secondary/50 rounded-lg px-3 py-1.5">
              <span className="text-[11px] text-secondary-foreground font-medium">Mostrando colección</span>
              <button onClick={() => setCollectionFilter(null)} className="text-[11px] text-primary font-medium">Ver todas</button>
            </div>
          )}

          {/* Recipe list */}
          {filtered.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground text-xs">No se encontraron recetas.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map(r => (
                <RecipeCard
                  key={r.id}
                  recipe={r}
                  isFavorite={favorites.includes(r.id)}
                  onToggleFavorite={() => toggleFavorite(r.id)}
                  onSelect={() => setSelectedRecipe(r.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ========== TAB 2: Guía ========== */}
      {activeTab === 'guia' && (
        <div className="space-y-2.5">
          <GuideSectionCard
            icon="🟢"
            title="Alimentos para Priorizar"
            items={[
              "Frutas variadas (manzana, plátano, frutos rojos)",
              "Vegetales y hojas verdes en cada comida",
              "Granos integrales: avena, arroz integral, quinoa",
              "Legumbres: frijoles, lentejas, garbanzos",
              "Yogur natural y alimentos fermentados",
              "Aceite de oliva extra virgen",
              "Semillas: chía, linaza, calabaza",
              "Agua natural en abundancia"
            ]}
            accent="bg-primary"
          />

          <GuideSectionCard
            icon="🟡"
            title="Alimentos para Reducir"
            items={[
              "Refrescos y bebidas azucaradas",
              "Ultraprocesados y embutidos",
              "Exceso de harina blanca y pan industrial",
              "Frituras y comida rápida",
              "Dulces con exceso de azúcar refinado",
              "Edulcorantes artificiales en exceso"
            ]}
            accent="bg-wellness-peach"
          />

          <GuideSectionCard
            icon="🍽️"
            title="Combinaciones Simples"
            items={[
              "Yogur + fruta + avena → desayuno completo",
              "Arroz integral + lentejas + ensalada → almuerzo equilibrado",
              "Fruta + nueces → snack satisfactorio",
              "Tostada integral + huevo + tomate → desayuno rápido",
              "Pollo + verduras asadas → cena nutritiva",
              "Plátano + mantequilla de maní → energía rápida"
            ]}
            accent="bg-wellness-blue"
          />

          <GuideSectionCard
            icon="⚡"
            title="Ideas para Días Ocupados"
            items={[
              "Desayuno rápido: overnight oats (prepara la noche anterior)",
              "Snack de emergencia: fruta + puñado de nueces",
              "Almuerzo express: wrap integral con proteína y vegetales",
              "Cena ligera: sopa de verduras o huevos revueltos con ensalada",
              "Hidratación fácil: agua con rodajas de limón y pepino"
            ]}
            accent="bg-wellness-purple"
          />

          <div className="bg-wellness-peach-light/60 rounded-xl p-3 border border-wellness-peach/20">
            <p className="text-[11px] font-semibold text-foreground mb-0.5">💡 Recuerda</p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              No se trata de perfección. Pequeños cambios sostenidos crean grandes resultados.
            </p>
          </div>
        </div>
      )}

      {/* ========== TAB 3: Favoritos ========== */}
      {activeTab === 'favoritos' && (
        <FavoritesList
          recipes={recipes}
          favorites={favorites}
          onToggleFavorite={toggleFavorite}
          onSelect={setSelectedRecipe}
        />
      )}
    </div>
  );
};

export default Recipes;